import { Injectable } from '@angular/core';
import { CHAT_INDEXED_DB_CONFIG } from '../enums/event.enum';

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  
  constructor() { }

  /**
   * Saves a file in the specified IndexedDB object store using the provided key.
   *
   * @param file - The file to store.
   * @param key - The key used to identify the stored file.
   * @param dbName - The name of the IndexedDB database. Defaults to "fileDb".
   * @param storeName - The name of the object store within the database. Defaults to "fileStore".
   *
   * @remarks
   * Creates the object store during upgrade if it does not already exist.
   * Errors during database open or transaction are logged to the console.
   */
  saveFile(file: File, key: string, dbName: string = "fileDb", storeName: string = "fileStore") {
    // Open without version to get current state
    const checkRequest = indexedDB.open(dbName);

      checkRequest.onsuccess = (event: any) => {
        const db = event.target.result;
        const storeExists = db.objectStoreNames.contains(storeName);
        const currentVersion = db.version;
        db.close();

        // If store exists, open normally, If store missing, bump version to trigger onupgradeneeded
        const request = indexedDB.open(dbName, storeExists ? currentVersion : currentVersion + 1);
        
        request.onupgradeneeded = (event: any) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName);
          }
        };

        request.onsuccess = (event: any) => {
          const db = event.target.result;
          const tx = db.transaction(storeName, 'readwrite');
          tx.objectStore(storeName).put(file, key);
          tx.oncomplete = () => db.close();
          tx.onerror = () => { console.error('Save failed:', tx.error); db.close(); };
        };
        
        request.onerror = () => console.error(`Failed to open DB: ${dbName}`);

      };

    checkRequest.onupgradeneeded = (event: any) => {
      // DB didn't exist at all, create store fresh
      const db = event.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName);
      }
    };

    checkRequest.onerror = () => console.error(`Failed to open DB: ${dbName}`);
  }

  /**
   * Retrieves a file from the specified IndexedDB object store by key.
   *
   * @param key - The key of the file to retrieve.
   * @param dbName - The name of the IndexedDB database. Defaults to "fileDb".
   * @param storeName - The name of the object store within the database. Defaults to "fileStore".
   * @returns A promise that resolves with the stored file.
   *
   * @remarks
   * The promise rejects with `null` if the read request fails.
   */
  getFile(key: string, dbName: string = "fileDb", storeName: string = "fileStore"): Promise<File> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName);

      request.onsuccess = (event: any) => {
        const db = event.target.result;
        const getReq = db.transaction(storeName, "readonly")
          .objectStore(storeName)
          .get(key);

        getReq.onsuccess = () => resolve(getReq.result);
        getReq.onerror = () => reject(null);
      };
    });
  }

  /**
   * Deletes a file entry from the specified IndexedDB object store.
   *
   * @param key - The key of the file to delete from the object store.
   * @param dbName - The name of the IndexedDB database. Defaults to "fileDb".
   * @param storeName - The name of the object store within the database. Defaults to "fileStore".
   *
   * @remarks
   * If the specified object store does not exist, the method will close the database and exit.
   * Errors during database opening or transaction are logged to the console.
   */
  deleteFile(key: string, dbName: string = "fileDb", storeName: string = "fileStore") {
    const request = indexedDB.open(dbName);

    request.onerror = () => console.error(`Failed to open DB: ${dbName}`);

    request.onsuccess = (event: any) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(storeName)) {
        db.close();
        return;
      }

      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).delete(key);

      tx.oncomplete = () => db.close();
      tx.onerror = () => { console.error(`Delete failed:`, tx.error); db.close(); };
    };
  }

  /**
  * Removes file store data from IndexedDB.
  * This method deletes the stored file entry from the configured
  * IndexedDB database and object store using the predefined keys.
  */
  clearIndexedDbStore(): void {
    this.deleteFile(
      CHAT_INDEXED_DB_CONFIG.FIELD_NAME,
      CHAT_INDEXED_DB_CONFIG.DB_NAME,
      CHAT_INDEXED_DB_CONFIG.STORE_NAME
    );
  }
}